import { useFlags } from "launchdarkly-react-client-sdk";
import React, { createContext, useCallback, useEffect, useState } from "react";

import type { TestHarnessPubSub } from "@storyboard/web-client/test-harness";
import { type FlagData, flags as GlobalFlags, notifyFlagChange } from "@/config";
import { activeTestHarness } from "@/init/testHarness";
import PubSub from "@/PubSub";

export type Config = {
  baseDomain: string;
  graphqlApiKey: string;
  graphqlUrl: string;
  sessionRecordingLength: number;
  tusUrl: string;
};

type AppState = {
  audioCtx?: AudioContext;
  config?: Config;
  flags: FlagData;
  pubSub?: TestHarnessPubSub;
  redirectPath?: string;
  setFlags: (overrides: Partial<FlagData>) => void;
  clearFlagOverrides: () => void;
  /** Flag values as LaunchDarkly serves them, before any debug override. */
  launchDarklyFlags: FlagData;
  /** Keys the debug menu has overridden in this page load. */
  overriddenFlagKeys: ReadonlySet<string>;
  /** Drops one override, so the key reads its LaunchDarkly value again. */
  clearFlagOverride: (key: string) => void;
  setRedirectPath: (path: string) => void;
  tracking?: any;
};

export const AppContext = createContext<AppState>({
  setRedirectPath: () => {},
  flags: {},
  setFlags: (_) => {},
  clearFlagOverrides: () => {},
  launchDarklyFlags: {},
  overriddenFlagKeys: new Set(),
  clearFlagOverride: (_) => {},
});

type Props = {
  baseDomain?: string;
  children: React.ReactNode | React.ReactNode[];
  graphqlApiKey: string;
  graphqlUrl: string;
  sessionRecordingLength: number;
  tusUrl: string;
  initialFlags: FlagData;
};

const AppStateProvider = ({
  baseDomain,
  tusUrl,
  graphqlUrl,
  graphqlApiKey,
  children,
  sessionRecordingLength,
  initialFlags,
}: Props) => {
  const [flags, setFlagsState] = useState<FlagData>(initialFlags);
  const [redirectPath, setRedirectPath] = useState<string>("");
  // State, not a ref, so the debug menu re-renders when an override is added or dropped.
  const [overriddenFlagKeys, setOverriddenFlagKeys] = useState<ReadonlySet<string>>(new Set());
  const ldFlags = useFlags();
  // `initialFlags` is the module-level `flags` object, which the effect below overwrites with every current value,
  // so the build defaults are copied once here, before any override reaches it.
  const [buildDefaults] = useState<FlagData>(() => ({ ...initialFlags }));

  const setFlags = useCallback((overrides: Partial<FlagData>) => {
    setOverriddenFlagKeys((prev) => new Set([...prev, ...Object.keys(overrides)]));
    setFlagsState((prev) => ({ ...prev, ...overrides }));
  }, []);

  /** The value a key falls back to without an override: LaunchDarkly's when it serves the key, else the build default. */
  const unoverriddenValue = useCallback(
    (key: string) => (key in ldFlags ? ldFlags[key] : buildDefaults[key]),
    [ldFlags, buildDefaults],
  );

  const clearFlagOverrides = useCallback(() => {
    setFlagsState((prev) => {
      const restored: FlagData = { ...prev, ...ldFlags };
      for (const key of overriddenFlagKeys) restored[key] = unoverriddenValue(key);
      return restored;
    });
    setOverriddenFlagKeys(new Set());
  }, [ldFlags, overriddenFlagKeys, unoverriddenValue]);

  const clearFlagOverride = useCallback(
    (key: string) => {
      setOverriddenFlagKeys((prev) => new Set([...prev].filter((overridden) => overridden !== key)));
      setFlagsState((prev) => ({ ...prev, [key]: unoverriddenValue(key) }));
    },
    [unoverriddenValue],
  );

  // Apply LaunchDarkly updates to every key the debug menu has not overridden.
  useEffect(() => {
    const merged: FlagData = { ...ldFlags };
    for (const key of overriddenFlagKeys) {
      delete merged[key];
    }
    // Keep the same object when nothing changed, so toggling an override does not notify flag listeners twice.
    setFlagsState((prev) =>
      Object.keys(merged).some((key) => prev[key] !== merged[key]) ? { ...prev, ...merged } : prev,
    );
  }, [ldFlags, overriddenFlagKeys]);

  useEffect(() => {
    for (const flag in flags) {
      GlobalFlags[flag] = flags[flag];
    }
    notifyFlagChange();
  }, [flags]);

  // Browser-test seam: harness-enabled builds substitute the injected AppSync fake for the Amplify-backed PubSub.
  const pubSub = React.useMemo(
    () => activeTestHarness()?.pubSub ?? new PubSub(graphqlUrl, graphqlApiKey),
    [graphqlUrl, graphqlApiKey],
  );

  const appState: AppState = {
    redirectPath,
    setRedirectPath,
    flags,
    setFlags,
    clearFlagOverrides,
    launchDarklyFlags: ldFlags,
    overriddenFlagKeys,
    clearFlagOverride,
    pubSub,
    config: {
      baseDomain,
      graphqlApiKey,
      graphqlUrl,
      sessionRecordingLength,
      tusUrl,
    },
  };

  return <AppContext.Provider value={appState}>{children}</AppContext.Provider>;
};
export default AppStateProvider;
